import { type ApolloQueryResult } from '@apollo/client';
import { captureException } from '@sentry/browser';
import { useLazyQuery } from '@vue/apollo-composable';
import DUMMY from 'gql/dummy.gql';
import GET_CATALOG_PRODUCT_DATA_BY_ID from 'gql/getCatalogProductDataById.gql';
import parseNumber from 'utils/parseNumber';

import type { Cart } from '@/composables/shoppingcart';
import {
  type GetBestPriceQuery,
  type GetBestPriceQueryVariables,
  type GetCatalogProductDataByIdQuery,
  type GetCatalogProductDataByIdQueryVariables,
} from '@/generated/graphql';
import type { FischerPlugin } from '@/lib/fischerPlugin';

import { type SendEvent, useEventHub } from './eventHub';
import { useFischer } from './fischerPlugin';

// @ts-ignore
const GET_BESTPRICE = () => import('gql/getBestPrice.gql');

export type TrackingEventItem = {
  currency?: string;
  item_name?: string;
  item_id?: string;
  item_brand?: string;
  item_list_name?: string;
  item_list_id?: string;
  index?: number;
  item_variant?: string;
  price?: number;
  productPrice?: number;
  productId?: string;
  quantity?: number;
  cart_id?: number | string;
};

export interface TrackingProductOrCategory {
  CategoryRole?: string;
  name?: string;
  CategoryName?: string;
  id?: string;
  CategoryDisplayNamePath: string[];
}

export const getCategories = (paths: string[]): Record<string, string> => {
  const categories: Record<string, any> = {};
  if (paths?.length) {
    paths.forEach((el: any, idx: number) => {
      categories[idx === 0 ? 'item_category' : `item_category${idx + 1}`] = el;
    });
  }
  return categories;
};

export const addToDataLayer = (
  event: string,
  items: TrackingEventItem[],
  ecommerce?: object,
): void => {
  if (IS_SSR) {
    return;
  }

  if (typeof window.gtag === 'function') {
    window.gtag('event', event, {
      ...ecommerce,
      items,
    });
  } else {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: event,
      ecommerce: {
        ...ecommerce,
        items,
      },
    });
  }
};

const getProductById = async (
  productId: string,
  getProductByIdQuery: (
    variables: GetCatalogProductDataByIdQueryVariables,
  ) =>
    | Promise<ApolloQueryResult<GetCatalogProductDataByIdQuery>>
    | Promise<GetCatalogProductDataByIdQuery>
    | undefined,
  fischer: FischerPlugin,
) => {
  try {
    let result;
    do {
      result = await getProductByIdQuery({
        baseSiteId: fischer.baseSiteId(),
        apiLang: fischer.catalogLocale(),
        id: productId,
      });
      if (!result) {
        console.error('received no result for catalog product data');
        return null;
      }
    } while ('loading' in result && result.loading);
    const data = 'data' in result ? result.data : result;

    return data?.getCatalogProductDataById || null;
  } catch (error) {
    console.error('error getting product data', productId, error);
    return null;
  }
};

export const trackAddToCartFunction = async (
  cartId: number | string,
  productIdUnpadded: string,
  qty: number,
  fromMemoList: boolean,
  actionType: string | undefined,
  enableTracking: boolean,
  enableTrackingShop: boolean,
  getProductByIdQuery: (
    variables: GetCatalogProductDataByIdQueryVariables,
  ) =>
    | Promise<ApolloQueryResult<GetCatalogProductDataByIdQuery>>
    | Promise<GetCatalogProductDataByIdQuery>
    | undefined,
  fischer: FischerPlugin,
  getPriceForTracking: (
    productId: string,
    quantity: number,
  ) => ReturnType<typeof getPriceForTrackingFunction>,
  sendEvent: SendEvent,
): Promise<void> => {
  try {
    if (!(enableTracking && enableTrackingShop)) {
      return;
    }

    const productId = productIdUnpadded.padStart(6, '0');
    const product = await getProductById(productId, getProductByIdQuery, fischer);
    if (!product) {
      return;
    }

    const ecomLang = product.TextList?.find((el: any) => el.Key === 'ECOMLANG')?.Value;

    const item: TrackingEventItem = {
      item_name: ecomLang || undefined,
      item_id: productId,
      item_brand: fischer.brandName(),
      item_variant: product.ProductName,
      quantity: qty,
      ...getCategories(product.CategoryDisplayNamePath || []),
    };

    const { price, currency, totalPrice } = await getPriceForTracking(productId, qty);
    if (price) {
      item.price = price;
    }

    let ecommerce;
    if (totalPrice) {
      ecommerce = {
        currency: currency,
        value: totalPrice,
      };
    }
    addToDataLayer('add_to_cart', [item], ecommerce);
    const items: TrackingEventItem[] = fischer.shop.isClosedShop()
      ? [
          {
            productId: productId,
            quantity: qty,
            productPrice: totalPrice,
            currency,
          },
        ]
      : [
          {
            productId: productId,
          },
        ];

    const content: {
      cartId: typeof cartId;
      items: typeof items;
      actionType?: string;
    } = {
      cartId,
      items,
    };
    if (actionType) {
      content.actionType = actionType;
    }

    sendEvent(fromMemoList ? 'memo_list_cart_single' : 'add_to_cart', content);
  } catch (error) {
    console.error(error);
    captureException(error);
  }
};

export const getCartItemsFunction = (cart: Cart, fischer: FischerPlugin): TrackingEventItem[] => {
  const items: TrackingEventItem[] = [];
  if (cart?.products?.length) {
    cart.products.forEach((product: any) => {
      const productData = product.catalogProductData || {};
      const item: TrackingEventItem = {
        item_id: product.articleNo,
        item_name: product.name,
        item_brand: fischer.brandName(),
        item_variant: productData.ProductName,
        ...getCategories(productData.CategoryDisplayNamePath),
      };

      if (isShopFunction(fischer)) {
        item.price = product.priceValue / product.initialQuantity;
        item.quantity = product.quantity;
      }

      items.push(item);
    });
  }

  return items;
};

export const isShopFunction = (fischer: FischerPlugin): boolean =>
  fischer.shop.isClosedShop() || fischer.shop.isOnlineShop();

export const enableTrackingFunction = (fischer: FischerPlugin): boolean =>
  fischer.enableAnalytics();

export const enableTrackingShopFunction = (fischer: FischerPlugin): boolean =>
  isShopFunction(fischer) || fischer.shop.isRetailConnectCart();

export const getPriceForTrackingFunction = async (
  productIdUnpadded: string,
  qty: number = 0,
  fischer: FischerPlugin,
  getBestPriceQuery: (
    variables: GetBestPriceQueryVariables,
  ) => Promise<ApolloQueryResult<GetBestPriceQuery>> | Promise<GetBestPriceQuery> | undefined,
): Promise<{
  price: number | undefined;
  quantity: number;
  currency?: string;
  totalPrice: number | undefined;
}> => {
  if (isShopFunction(fischer) && productIdUnpadded) {
    try {
      const productId = productIdUnpadded.padStart(6, '0');
      let result;
      do {
        result = await getBestPriceQuery({
          baseSiteId: fischer.baseSiteId(),
          accessToken: fischer.user.accessToken(),
          productCode: productId,
          qty: qty,
          language: fischer.siteLanguage(),
          expired: true,
          userId: fischer.user.id(),
        });

        if (!result) {
          throw new Error('did not get result for best price');
        }
      } while ('loading' in result && result.loading);

      const data = result && 'data' in result ? result.data : result;
      const bestPrice = data?.getBestPrice;
      if (
        bestPrice &&
        bestPrice.price?.valueFormatted &&
        bestPrice.price?.totalValueFormatted &&
        bestPrice.initialQuantity
      ) {
        return {
          currency: bestPrice.price.currencyIso || undefined,
          price: parseNumber(bestPrice.price.valueFormatted) / bestPrice.initialQuantity,
          quantity: bestPrice.initialQuantity || 1,
          totalPrice: parseNumber(bestPrice.price.totalValueFormatted),
        };
      }

      return { price: undefined, quantity: qty, currency: '', totalPrice: undefined };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.trace('error getting best price', error, productIdUnpadded, qty);
    }
  }

  return { price: undefined, quantity: qty, currency: '', totalPrice: undefined };
};

export const trackAddMultipleToCartFunction = async (
  cartId: number | string,
  productsWithQuantity: {
    id: string;
    quantity: number;
  }[],
  fischer: FischerPlugin,
  getBestPriceQuery: Parameters<typeof getPriceForTrackingFunction>[3],
  sendEvent: (
    type: string,
    data: {
      cartId: string | number;
      items: TrackingEventItem[];
    },
  ) => Promise<void>,
) => {
  try {
    if (!(enableTrackingFunction(fischer) && enableTrackingShopFunction(fischer))) {
      return;
    }

    const itemsWithNull: (TrackingEventItem | null)[] = await Promise.all(
      productsWithQuantity.map(async ({ id: productId, quantity }: any) => {
        try {
          const item: TrackingEventItem = {
            productId,
            quantity,
          };
          const { totalPrice, currency } = await getPriceForTrackingFunction(
            productId,
            quantity,
            fischer,
            getBestPriceQuery,
          );
          if (totalPrice) {
            item.productPrice = totalPrice;
            item.currency = currency;
          }
          return item;
        } catch (error) {
          console.error('could not get info on product', productId, quantity, error);
          return null;
        }
      }),
    );
    const items = itemsWithNull.filter((item) => item) as TrackingEventItem[];

    if (items.length) {
      sendEvent('add_to_cart', {
        cartId,
        items,
      });
    }
  } catch (error) {
    console.error(error);
    captureException(error);
  }
};

export const useTracking = () => {
  const fischer = useFischer();
  const { sendEvent } = useEventHub();

  const enableTracking = () => enableTrackingFunction(fischer);

  const enableTrackingShop = () => enableTrackingShopFunction(fischer);

  const isShop = () => isShopFunction(fischer);

  type getPriceForTrackingParams = Parameters<typeof getPriceForTrackingFunction>;
  const { load: loadGetBestPrice, refetch: refetchGetBestPrice } =
    useLazyQuery<GetBestPriceQuery>(DUMMY);
  const getPriceForTracking = async (
    productId: getPriceForTrackingParams[0],
    quantity: getPriceForTrackingParams[1],
  ) => {
    const query = (await GET_BESTPRICE()).default;
    return getPriceForTrackingFunction(
      productId,
      quantity,
      fischer,
      (variables) => loadGetBestPrice(query, variables) || refetchGetBestPrice(variables),
    );
  };

  const trackAddMultipleToCart = async (
    cartId: number | string,
    productsWithQuantity: {
      id: string;
      quantity: number;
    }[],
  ) => {
    const query = (await GET_BESTPRICE()).default;
    return trackAddMultipleToCartFunction(
      cartId,
      productsWithQuantity,
      fischer,
      (variables) => {
        const promise = loadGetBestPrice(query, variables) || refetchGetBestPrice(variables);
        if (!promise) {
          throw new Error('could not get best price');
        }
        return promise;
      },
      sendEvent,
    );
  };

  const { load: loadGetCatalogProductDataById, refetch: refetchGetCatalogProductDataById } =
    useLazyQuery<GetCatalogProductDataByIdQuery>(GET_CATALOG_PRODUCT_DATA_BY_ID);
  const trackAddToCart = async (
    cartId: number | string,
    productIdUnpadded: string,
    qty: number,
    fromMemoList: boolean,
    actionType?: string,
  ) =>
    trackAddToCartFunction(
      cartId,
      productIdUnpadded,
      qty,
      fromMemoList,
      actionType,
      enableTracking(),
      enableTrackingShop(),
      (variables) =>
        loadGetCatalogProductDataById(null, variables) ||
        refetchGetCatalogProductDataById(variables),
      fischer,
      getPriceForTracking,
      sendEvent,
    );

  const trackViewItemList = (
    productList: TrackingProductOrCategory[],
    isPDP: boolean = false,
  ): void => {
    try {
      if (IS_SSR || !enableTracking()) {
        return;
      }

      const items: TrackingEventItem[] = [];
      productList.forEach((product: any, index: number) => {
        if (!product) {
          return;
        }

        const isFamily = product.CategoryRole === 'FAMILY_PAGE';
        const item: TrackingEventItem = {
          item_name: product.name || product.ProductName || product.CategoryName,
          item_id: isFamily ? product.id || 'FP' : product.ProductId || product.id,
          item_brand: fischer.brandName(),
          item_list_name: isPDP ? 'accessories' : 'Featured Items',
          item_list_id: isPDP ? 'A-PDP' : window.location.pathname,
          index: index + 1,
          ...getCategories(product.CategoryDisplayNamePath),
        };

        items.push(item);
      });

      if (items.length) {
        addToDataLayer('view_item_list', items);
      }
    } catch (error) {
      console.error(error);
      captureException(error);
    }
  };

  const trackSelectItem = async (
    toTrack: TrackingProductOrCategory,
    isPDP: boolean = false,
  ): Promise<void> => {
    try {
      if (!enableTracking()) {
        return;
      }

      const isFamily = toTrack.CategoryRole === 'FAMILY_PAGE';
      if (!toTrack.CategoryRole || isFamily) {
        const item: TrackingEventItem = {
          item_name: toTrack.name || toTrack.CategoryName,
          item_id: isFamily ? toTrack.id || 'FP' : toTrack.id,
          item_brand: fischer.brandName(),
          item_list_name: isPDP ? 'accessories' : 'Featured Items',
          item_list_id: isPDP ? 'A-PDP' : window.location.pathname,
          index: 1,
          ...getCategories(toTrack.CategoryDisplayNamePath),
        };
        addToDataLayer('select_item', [item]);
      }
    } catch (error) {
      console.error(error);
      captureException(error);
    }
  };

  const getCartItems = (cart: Cart) => getCartItemsFunction(cart, fischer);

  return {
    enableTracking,
    enableTrackingShop,
    isShop,
    getCartItems,
    getPriceForTracking,
    trackAddMultipleToCart,
    trackAddToCart,
    trackViewItemList,
    trackSelectItem,
  };
};
